/**
 * WhatsApp Chat Store V2 — Slice composition
 *
 * Lo store è splittato in 9 slice tematici per migliorare manutenibilità.
 * L'API pubblica resta IDENTICA al vecchio `whatsappChatStore.ts` monolitico:
 * tutti i campi state e tutte le actions sono accessibili dallo stesso `useWhatsAppChatStore`.
 *
 * Struttura:
 * - configSlice         — wahaConfig + loadConfig
 * - chatListSlice       — chats, loadChats, refreshChats, addChatToStore, loadAllChatsCache
 * - chatSelectionSlice  — selectedChatId, selectChat
 * - messagesSlice       — messages, loadMessages, addMessage, updateMessage*
 * - sendSlice           — pendingMessages, sendText/Media/Location/Contact/Poll, retry
 * - messageActionsSlice — delete/edit/react/forward/pin
 * - presenceSlice       — presenceMap, lidToCusMap, updatePresence, saveLidMapping
 * - readStatusSlice     — sendReadReceipt, loadUnreadCountsFromDb
 * - draftReplySlice     — drafts, replyingTo
 *
 * Cross-slice via Zustand `get()` (read) e `set()` (merge multi-slice).
 */

import { create } from 'zustand';
import type { WhatsAppChatStore } from './types';

import { createConfigSlice } from './slices/configSlice';
import { createChatListSlice } from './slices/chatListSlice';
import { createChatSelectionSlice } from './slices/chatSelectionSlice';
import { createMessagesSlice } from './slices/messagesSlice';
import { createSendSlice } from './slices/sendSlice';
import { createMessageActionsSlice } from './slices/messageActionsSlice';
import { createPresenceSlice } from './slices/presenceSlice';
import { createReadStatusSlice } from './slices/readStatusSlice';
import { createDraftReplySlice } from './slices/draftReplySlice';

// Initial state combinato (per la reset action)
const initialState = {
  // configSlice
  wahaConfig: null,
  configLoading: false,
  configError: null,

  // chatListSlice
  chats: [],
  chatsLoading: false,
  chatsError: null,
  _loadChatsAttempt: 0,
  chatsOffset: 0,
  chatsHasMore: true,
  chatsLoadedForConfig: null,
  chatsLoadingMore: false,
  allChatsCache: null,
  allChatsCacheTimestamp: 0,

  // chatSelectionSlice
  selectedChatId: null,
  selectedChatData: null,

  // messagesSlice
  messages: [],
  messagesLoading: false,
  messagesError: null,
  loadOlderError: null,
  hasOlderMessages: true,
  oldestMessageTimestamp: null,
  wahaOffset: 0,
  messagesSource: 'waha' as const,
  dbOldestTimestamp: null,

  // sendSlice
  pendingMessages: [],
  sendingMessage: false,
  uploadingMedia: false,

  // presenceSlice
  presenceMap: new Map(),
  lidToCusMap: new Map(),

  // draftReplySlice
  drafts: {},
  replyingTo: null,
};

export const useWhatsAppChatStore = create<WhatsAppChatStore>()((...a) => ({
  ...createConfigSlice(...a),
  ...createChatListSlice(...a),
  ...createChatSelectionSlice(...a),
  ...createMessagesSlice(...a),
  ...createSendSlice(...a),
  ...createMessageActionsSlice(...a),
  ...createPresenceSlice(...a),
  ...createReadStatusSlice(...a),
  ...createDraftReplySlice(...a),

  // Root reset action — resetta tutti i campi state allo stato iniziale.
  // NON pulisce le module-level cache (per quello c'è clearWhatsAppChatCaches).
  reset: () => {
    a[0](initialState as Partial<WhatsAppChatStore>);
  },
}));

export { clearWhatsAppChatCaches } from './caches';
